import { useState } from 'react';
import { Search, Download, CheckCircle, XCircle, Clock, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentOversight = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [rejectionModal, setRejectionModal] = useState({ open: false, transactionId: null });
  const [rejectionReason, setRejectionReason] = useState('');

  const mockTransactions = [
    {
      id: 'TRX-2025-001',
      user: 'Carlos Martínez',
      amount: 125000,
      paymentMethod: 'Nequi',
      paymentDetails: {
        accountNumber: '3001234567'
      },
      status: 'pending',
      date: '2025-12-10 14:30'
    },
    {
      id: 'TRX-2025-002',
      user: 'María González',
      amount: 250000,
      paymentMethod: 'Transferencia Bancaria',
      paymentDetails: {
        bankEntity: 'Bancolombia',
        accountType: 'Ahorros',
        accountHolder: 'María González Pérez',
        holderId: '1234567890'
      },
      status: 'approved',
      date: '2025-12-11 09:15'
    },
    {
      id: 'TRX-2025-003',
      user: 'Pedro Sánchez',
      amount: 180000,
      paymentMethod: 'Nequi',
      paymentDetails: {
        accountNumber: '3109876543'
      },
      status: 'rejected',
      rejectionReason: 'Número de cuenta inválido. Por favor verifica los datos.',
      date: '2025-12-09 16:45'
    }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    })?.format(value);
  };

  const statusConfig = {
    pending: {
      label: 'Pendiente',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      icon: Clock
    },
    approved: {
      label: 'Aprobado',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      icon: CheckCircle
    },
    rejected: {
      label: 'Rechazado',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      icon: XCircle
    }
  };

  const handleApprove = (transactionId) => {
    console.log('Approve transaction:', transactionId);
    // TODO: Update transaction status to approved
    // TODO: Update user's totalWithdrawn in Bizznet Rewards
    // TODO: Send notification to user's BizznetNotifications
  };

  const openRejectionModal = (transactionId) => {
    setRejectionModal({ open: true, transactionId });
    setRejectionReason('');
  };

  const closeRejectionModal = () => {
    setRejectionModal({ open: false, transactionId: null });
    setRejectionReason('');
  };

  const handleReject = () => {
    if (!rejectionReason?.trim()) {
      alert('Por favor ingresa una observación');
      return;
    }
    console.log('Reject transaction:', rejectionModal?.transactionId, 'Reason:', rejectionReason);
    // TODO: Update transaction status to rejected with reason
    // TODO: Send notification to user's BizznetNotifications with rejection reason
    closeRejectionModal();
  };

  const toggleMethodDetails = (transactionId) => {
    setExpandedMethod(expandedMethod === transactionId ? null : transactionId);
  };

  const handleDownloadReceipt = (receipt) => {
    console.log('Download receipt:', receipt);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Pagos, Transacciones y Retiros
        </h1>
        <p className="text-muted-foreground">
          Gestiona solicitudes de retiro de Bizznet Rewards
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm text-muted-foreground">Pendientes</span>
          </div>
          <p className="text-2xl font-bold text-foreground">8</p>
        </div>

        <div className="bg-card rounded-lg border shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm text-muted-foreground">Aprobados Hoy</span>
          </div>
          <p className="text-2xl font-bold text-foreground">24</p>
        </div>

        <div className="bg-card rounded-lg border shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-50 rounded-lg">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm text-muted-foreground">Total Procesado</span>
          </div>
          <p className="text-2xl font-bold text-foreground">$1.8M</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Buscar por usuario o método..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
            leftIcon={<Search className="w-4 h-4 text-muted-foreground" />}
          />
        </div>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="w-full sm:w-48"
        >
          <option value="all">Todos los Estados</option>
          <option value="pending">Pendientes</option>
          <option value="approved">Aprobados</option>
          <option value="rejected">Rechazados</option>
        </Select>
        <Button iconName="Download" variant="outline">
          Exportar
        </Button>
      </div>

      <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Método
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {mockTransactions?.map((transaction) => {
                const status = statusConfig?.[transaction?.status];
                const StatusIcon = status?.icon;
                const isExpanded = expandedMethod === transaction?.id;

                return (
                  <tr key={transaction?.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-foreground">{transaction?.user}</p>
                      <p className="text-xs text-muted-foreground">{transaction?.date}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-semibold text-foreground">
                        {formatCurrency(transaction?.amount)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleMethodDetails(transaction?.id)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        {transaction?.paymentMethod}
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs space-y-1">
                          {transaction?.paymentMethod === 'Nequi' ? (
                            <div>
                              <p className="font-semibold text-foreground">Número de Cuenta:</p>
                              <p className="text-muted-foreground">{transaction?.paymentDetails?.accountNumber}</p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div>
                                <p className="font-semibold text-foreground">Entidad Bancaria:</p>
                                <p className="text-muted-foreground">{transaction?.paymentDetails?.bankEntity}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">Tipo de Cuenta:</p>
                                <p className="text-muted-foreground">{transaction?.paymentDetails?.accountType}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">Titular:</p>
                                <p className="text-muted-foreground">{transaction?.paymentDetails?.accountHolder}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">Número de Identificación:</p>
                                <p className="text-muted-foreground">{transaction?.paymentDetails?.holderId}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full ${status?.bgColor} ${status?.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleApprove(transaction?.id)}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                          title="Aprobar Retiro"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Aprobado
                        </button>
                        <button
                          onClick={() => openRejectionModal(transaction?.id)}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                          title="Rechazar Retiro"
                        >
                          <XCircle className="w-4 h-4" />
                          Rechazar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rejection Modal */}
      {rejectionModal?.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Rechazar Retiro</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Por favor, ingresa la observación del rechazo. Esta información será enviada al usuario.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              placeholder="Ej: Número de cuenta inválido. Por favor verifica los datos y vuelve a intentar."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={closeRejectionModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
              >
                Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentOversight;